import { useState } from "react";
import useSWR, { mutate } from "swr";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { orange } from "@material-ui/core/colors";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import StatusIcon from "components/StatusIcon";
import RefreshIcon from "@material-ui/icons/Refresh";
import apiRequest, { getErrorCode } from "utils/apiRequestWithErrorCode";

import { SpfCheckResponse } from "@dashboard-v3/api";

type Params = { domainName: string };

export default function SpfProperty({ domainName }: Params) {
  const { t } = useTranslation("orgAndDomains");
  const [showSPFWarning, setShowSPFWarning] = useState(false);
  const spfUri = `/domains/${domainName}/spf`;
  const { data, error, isLoading, isValidating } = useSWR(spfUri, spfChecker, {
    revalidateOnFocus: false,
    revalidate: true,
  });

  const handleRefresh = async e => {
    e.stopPropagation();
    await mutate(spfUri);
    setShowSPFWarning(true);
  };

  if (error)
    return (
      <Typography variant="body2">
        {t([
          `error.getSpfStatus.${getErrorCode<"DOMAIN_NOT_FOUND">(error)}`,
          "error.getSpfStatus.SYSTEM_FAILURE",
        ])}
      </Typography>
    );

  if (!data || isLoading) return <CircularProgress size={12} />;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <StatusIcon
        state={data.enable ? "enable" : "disable"}
        data-testid="spfEnabledIndicator"
      />
      <Typography variant="body2">
        {t(data.enable ? "list.spfIsSet" : "list.spfNeedsConfig")}
      </Typography>
      <Tooltip title={t("list.howToSetupSpf")}>
        <IconButton
          href={SUPPORT_URL}
          target="_blank"
          size="small"
          onClick={event => event.stopPropagation()}
        >
          <OpenInNewIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      {!data.enable && (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Tooltip title={t("spfRefreshBtn")}>
            {isValidating ? (
              <CircularProgress size={20} />
            ) : (
              <IconButton
                size="small"
                onClick={handleRefresh}
                style={{ cursor: "pointer" }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Tooltip>
          {showSPFWarning && (
            <Typography
              variant="caption"
              style={{ paddingTop: 5, marginLeft: 5, color: orange[500] }}
            >
              {t("spfRefreshWarning")}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}

function spfChecker(path: string) {
  return apiRequest<SpfCheckResponse>("GET", path);
}

const SUPPORT_URL =
  "https://support.mxhero.com/hc/en-us/articles/201093935-Improving-Delivery-with-SPF";
